/**
 * Catálogo das telas que o usuário pode abrir (JOR4-7663, RN04).
 *
 * A lista de telas permitidas é montada uma única vez, pelo menu lateral, a
 * partir de `menus-allowed`. A personalização da tela de Início precisa da
 * mesma lista para oferecer os itens de acesso rápido, e ela não tem como
 * alcançar a instância do menu — daí este módulo, que guarda o que o menu já
 * carregou em vez de repetir a requisição (e correr o risco de as duas listas
 * divergirem).
 *
 * A publicação é assíncrona: a aba de Início abre dentro do `render()` do menu,
 * antes de `menus-allowed` responder. Por isso quem consome **assina** o
 * catálogo em vez de pedi-lo uma vez, e o menu publica SEMPRE — inclusive lista
 * vazia quando a carga falha, para ninguém ficar pendurado num "carregando"
 * eterno.
 *
 * Assinatura, e não promessa, por causa da troca de empresa: ela reconstrói o
 * layout sem recarregar a página, então o estado deste módulo sobrevive. Com
 * uma promessa resolvida uma vez, a Início da empresa nova recebia o catálogo
 * da anterior — e nunca mais era corrigida quando o `menus-allowed` da empresa
 * nova chegava. Daí também o `reiniciarCatalogo()`, que devolve todo mundo ao
 * "carregando" no instante em que o layout é remontado: melhor um vazio de um
 * segundo do que telas de outra empresa oferecidas como permitidas. (RN04)
 */

import type { LucideIcon } from "lucide-react";
import { montarArvore, type No } from "./arvoreMenu";
import { iconeDoMenu } from "./iconesMenu";
import type { ItemMenu } from "./montarMenu";

/** Uma tela do menu, no formato que o acesso rápido consome. */
export interface TelaSelecionavel {
    /** TLA_CLASSEGRID: identifica a tela no ControleTabPadrao e na preferência. */
    classInvoke: string;
    titulo: string;
    /** Caminho dos grupos até a tela (ex.: "Cadastros › Motoristas"). */
    trilha: string;
    icone: LucideIcon;
}

const SEPARADOR_TRILHA = " › ";

/** Lista publicada, ou `null` enquanto o menu não respondeu. */
export type Catalogo = ItemMenu[] | null;

/** Recebe o catálogo a cada mudança, `null` incluído. */
export type OuvinteCatalogo = (catalogo: Catalogo) => void;

let catalogo: Catalogo = null;
const ouvintes = new Set<OuvinteCatalogo>();

const notificar = (): void => {
    // Cópia: um ouvinte pode se desinscrever de dentro da própria notificação.
    for (const ouvinte of Array.from(ouvintes)) ouvinte(catalogo);
};

/** Publica a lista do menu. Chamado pelo MenuNavegacaoBase, e só por ele. */
export const publicarCatalogo = (itens: ItemMenu[]): void => {
    catalogo = itens;
    notificar();
};

/**
 * Volta ao estado "ainda não sei quais telas o usuário pode abrir".
 *
 * Chamado pelo menu ao (re)montar o layout — é o que impede o catálogo da
 * empresa anterior de continuar valendo durante a troca de empresa.
 */
export const reiniciarCatalogo = (): void => {
    catalogo = null;
    notificar();
};

/**
 * Assina o catálogo. O ouvinte é chamado na hora com o estado atual e a cada
 * publicação seguinte; devolve a função que cancela a assinatura.
 */
export const assinarCatalogo = (ouvinte: OuvinteCatalogo): (() => void) => {
    ouvintes.add(ouvinte);
    ouvinte(catalogo);

    return () => {
        ouvintes.delete(ouvinte);
    };
};

/** Só para os testes: descarta catálogo e assinaturas. */
export const redefinirCatalogo = (): void => {
    catalogo = null;
    ouvintes.clear();
};

/**
 * Telas abríveis do menu, na ordem em que aparecem nele — a mesma ordem que o
 * usuário já conhece, e não uma ordem alfabética que ele teria de reaprender.
 *
 * Grupos ficam de fora (não abrem tela) e a mesma classe aparece uma vez só:
 * o acesso rápido é chaveado por `classInvoke`, então uma tela repetida em dois
 * grupos viraria dois itens indistinguíveis na configuração.
 */
export const telasSelecionaveis = (itens: ItemMenu[]): TelaSelecionavel[] => {
    const telas: TelaSelecionavel[] = [];
    const vistas = new Set<string>();

    const visitar = (nos: No[], trilha: string[]) => {
        for (const no of nos) {
            const item = no.item;

            if (item.classInvoke && !vistas.has(item.classInvoke)) {
                vistas.add(item.classInvoke);
                telas.push({
                    classInvoke: item.classInvoke,
                    titulo: item.text,
                    trilha: trilha.join(SEPARADOR_TRILHA),
                    icone: iconeDoMenu(item),
                });
            }

            // Um nó com classe E filhos é raro, mas existe no cadastro
            // ("Permissões do sistema" é pai de "Novidades"). Ele entra como
            // tela e também é percorrido como grupo: perder as telas de dentro
            // seria pior do que listar o pai duas vezes.
            //
            // Isso só é seguro porque o `menus-allowed` passou a limpar o
            // TLA_CLASSEGRID do grupo que o usuário não pode visualizar — o
            // pai entra na resposta por ser pai, não por permissão. Sem aquele
            // guard, o acesso rápido ofereceria aqui uma tela negada. (RN04)
            if (no.filhos.length > 0) {
                visitar(no.filhos, trilha.concat([item.text]));
            }
        }
    };

    visitar(montarArvore(itens), []);

    return telas;
};
